<script setup>
import { ref, reactive, defineEmits, onMounted } from 'vue'
import { helpers, required } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import dayjs from 'dayjs'
import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanPembelianService from '@/services/LaporanPembelianService'
import ObatService from '@/services/ObatService'
import useVuelidate from '@vuelidate/core'
import SupplierService from '@/services/SupplierService'
import { saveAs } from 'file-saver'

const toast = useToast()
const laporanService = new LaporanPembelianService()
const obatService = new ObatService()
const supplierService = new SupplierService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingExport = ref(false)
let isLoadingExportExcel = ref(false)
let form = reactive({
  date_from: null,
  date_to: null,
  obat: null,
  supplier: null,
})
let submitted = ref(false)
let acObat = ref([])
let list_supplier = ref([])

onMounted(async () => {
  const res = await supplierService.get('?sort=nama')
  list_supplier.value = res.data.data
})

const rules = {
  date_from: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  date_to: {
    required: helpers.withMessage('Tanggal harus diisi.', required),
  },
  /*
  obat: {
    required: helpers.withMessage('Obat harus diisi.', required),
  },
  supplier: {
    required: helpers.withMessage('Supplier harus diisi.', required),
  },
  */
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

function searchObat(e) {
  obatService
    .get(`?filter[nama]=${e.query}&filter[status]=aktif`)
    .then((res) => {
      acObat.value = res.data.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Obat', toast)
    })
}

function onExport(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    isLoadingExport.value = false
    return
  }

  isLoadingExport.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')

  let obatId = 0
  let supplierId = 0

  if (form.obat) obatId = form.obat.id
  if (form.supplier) supplierId = form.supplier

  laporanService
    .exportLaporanPembelianBarangSupplier(
      obatId,
      supplierId,
      startDate,
      endDate
    )
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-pembelian-barang-supplier.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Laporan Pembelian Per Barang Per Supplier', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onExportExcel(isFormValid) {
  isLoadingExportExcel.value = true
  const startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  const endDate = dayjs(form.date_to).format('YYYY-MM-DD')

  let obatId = 0
  let supplierId = 0

  if (form.obat) obatId = form.obat.id
  if (form.supplier) supplierId = form.supplier

  submitted.value = true

  if (!isFormValid) {
    isLoadingExportExcel.value = false
    return
  }

  laporanService
    .exportLaporanPembelianBarangSupplierExcel(
      obatId,
      supplierId,
      startDate,
      endDate
    )
    .then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'laporan-pembelian-barang-supplier.xls')
      document.body.appendChild(link)
      link.click()
    })
    .catch((err) => {
      errorHandler(err, 'Laporan Pembelian Per Barang Per Supplier', this)
    })
    .finally(() => (this.isLoadingExportExcel = false))
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  submitted.value = false
  form.date_to = null
  form.date_from = null
  form.obat = null
  form.supplier = null
}
</script>

<template>
  <fx-report-container
    title="Laporan Daftar Pembelian Per Barang Per Supplier"
    :loading="isLoadingExport"
    @toggle="onToggle"
    @export="onExport"
  >
    <template #content>
      <form @submit.prevent="onExport(!v$.$invalid)">
        <div class="formgrid grid">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-6">
            <Dropdown
              v-model="form.supplier"
              :options="list_supplier"
              optionValue="id"
              optionLabel="nama"
              placeholder="Pilih Supplier"
              class="w-full"
              filter
              showClear
            />
          </div>
          <div class="field col-12 md:col-6">
            <AutoComplete
              v-model="form.obat"
              :min-length="2"
              :suggestions="acObat"
              field="nama"
              class="w-full"
              input-class="w-full"
              placeholder="Barang"
              autofocus
              @complete="searchObat($event)"
            >
            </AutoComplete>
          </div>
          <div class="field col-12 md:col-3">
            <Calendar
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-3">
            <Calendar
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-3">
            <Button
              :loading="isLoadingExport"
              type="submit"
              icon="pi pi-file-pdf"
              label="Cetak"
              class="mb-0 md:mb-2 xl:mb-0"
            />
            <Button
              :loading="isLoadingExportExcel"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2 md:ml-0 xl:ml-2 mb-0 md:mb-2 xl:mb-0"
              @click="onExportExcel(!v$.$invalid)"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 md:ml-0 xl:ml-2 mb-0 md:mb-2 xl:mb-0 btn-ml-0 btn-mt-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
